import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Input from "../form/input/InputField";
import {
  adminPaginationActiveClass,
  adminPaginationInactiveClass,
} from "../admin/adminStyles";
import BookingFormAuditSessionCards from "./BookingFormAuditSessionCards";
import CopyAuditJourneyButton from "./CopyAuditJourneyButton";
import {
  CARD_ENTRY_STATUS_LABELS,
  DEFAULT_SESSION_FILTERS,
  buildPageNumbers,
  getCardEntryStatus,
  getSessionCustomerProgress,
  filterSessions,
  formatDevice,
  formatEngagement,
  formatLandingPage,
  formatLocation,
  formatTrafficSource,
  getCustomerDisplayName,
  getFormattedProgressEntries,
  getRouteSummary,
  sortSessions,
  trafficSourceLabel,
  PROGRESS_FIELD_LABELS,
  type AuditSession,
  type SessionClientFilters,
  type SessionSortKey,
  type SortDirection,
} from "../../utils/bookingFormAuditSession";
import {
  BOOKING_SOURCE_SITE_NAMES,
  buildAbandonedBookingMailto,
  buildAbandonedSessionResumeUrl,
  copyTextToClipboard,
} from "../../utils/bookingResumeLink";
import {
  buildAuditCreateBookingState,
  hasAuditCreateBookingData,
} from "../../utils/mapAuditProgressToBookingForm";
import { formatAmount } from "../../utils/formatMoney";
import { formatUkDateTime } from "../../utils/dateUtils";
import {
  auditStepLabel,
  BOOKING_FUNNEL_STEPS,
  BOOKING_STEP_LABELS,
} from "../../utils/bookingFormAuditSteps";
import type { BookingFormAuditStats } from "./BookingFormAuditAnalytics";
import BookingFormAuditPrintReport, {
  triggerAuditReportPrint,
} from "./BookingFormAuditPrintReport";

const BOOKING_SOURCE_LABELS: Record<string, string> = {
  "southampton-port-taxi": "Southampton Port Taxi",
  "airport-transfers-taxi": "Airport Transfers Taxi",
  "uk-airport-taxi": "UK Airport Taxi",
  "liverpool-dock-taxi": "Liverpool Docks Taxi",
  "tikla-cars": "Tikla Cars",
};

const EVENT_LABELS: Record<string, string> = {
  form_started: "Form opened",
  step_viewed: "Step viewed",
  step_completed: "Step completed",
  field_updated: "Field updated",
  form_snapshot: "Form progress snapshot",
  validation_failed: "Validation failed",
  fare_quote_started: "Fare quote started",
  fare_quote_success: "Fare quote received",
  fare_quote_failed: "Fare quote failed",
  vehicle_selected: "Vehicle selected",
  confirm_started: "Confirm started",
  confirm_success: "Confirm success",
  confirm_failed: "Confirm failed",
  payment_step_entered: "Payment step",
  card_input_started: "Card input started",
  card_input_error: "Card input error",
  card_details_completed: "Card details completed",
  payment_intent_started: "Payment started",
  payment_intent_created: "Payment intent created",
  payment_succeeded: "Payment succeeded",
  payment_failed: "Payment failed",
  booking_created: "Booking created",
  booking_failed: "Booking save failed",
  page_viewed: "Page viewed",
  page_engagement: "Time on page",
  cta_clicked: "CTA clicked",
  nav_clicked: "Nav clicked",
  outbound_clicked: "Outbound link",
  call_clicked: "Call clicked",
  link_clicked: "Link clicked",
  language_popup_shown: "Language popup shown",
  language_selected: "Language selected",
  language_popup_dismissed: "Language popup dismissed",
};

const TABLE_COLUMNS: { key: SessionSortKey; label: string }[] = [
  { key: "website", label: "Website" },
  { key: "lastEventAt", label: "Activity" },
  { key: "route", label: "Trip" },
  { key: "customer", label: "Customer" },
  { key: "location", label: "Visitor" },
  { key: "trafficSource", label: "Source" },
  { key: "status", label: "Status" },
];

const TABLE_COL_COUNT = TABLE_COLUMNS.length + 2; // + Booking + Details

function formatSource(source: string): string {
  return BOOKING_SOURCE_LABELS[source] ?? source;
}

function formatEventType(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/_/g, " ");
}

function formatDateTime(value: string): string {
  try {
    return formatUkDateTime(value);
  } catch {
    return value;
  }
}

function metadataPreview(
  metadata: Record<string, unknown> | null,
  eventType?: string,
): string {
  if (!metadata || Object.keys(metadata).length === 0) return "";
  if (eventType === "page_engagement") {
    const parts: string[] = [];
    if (metadata.pagePath) parts.push(`Page: ${String(metadata.pagePath)}`);
    if (metadata.path) parts.push(`Page: ${String(metadata.path)}`);
    if (metadata.activeSec != null) parts.push(`Active: ${String(metadata.activeSec)}s`);
    if (metadata.scrollDepth != null) parts.push(`Scroll: ${String(metadata.scrollDepth)}%`);
    if (metadata.scrollDepthMax != null) parts.push(`Scroll: ${String(metadata.scrollDepthMax)}%`);
    if (metadata.pageExit != null) parts.push(metadata.pageExit ? "Exit" : "Heartbeat");
    return parts.join(" · ");
  }
  if (
    eventType === "cta_clicked" ||
    eventType === "nav_clicked" ||
    eventType === "outbound_clicked" ||
    eventType === "call_clicked" ||
    eventType === "link_clicked"
  ) {
    const parts: string[] = [];
    if (metadata.label) parts.push(String(metadata.label));
    if (metadata.placement) parts.push(`Placement: ${String(metadata.placement)}`);
    if (metadata.section) parts.push(`Section: ${String(metadata.section)}`);
    if (metadata.href) parts.push(`→ ${String(metadata.href)}`);
    if (metadata.path) parts.push(`On: ${String(metadata.path)}`);
    return parts.filter(Boolean).join(" · ");
  }
  if (eventType === "page_viewed") {
    const parts: string[] = [];
    if (metadata.path) parts.push(String(metadata.path));
    if (metadata.referrer) parts.push(`From: ${String(metadata.referrer)}`);
    return parts.join(" · ");
  }
  if (
    eventType === "language_popup_shown" ||
    eventType === "language_selected" ||
    eventType === "language_popup_dismissed"
  ) {
    const parts: string[] = [];
    if (metadata.fromLocale) parts.push(`From: ${String(metadata.fromLocale)}`);
    if (metadata.toLocale) parts.push(`To: ${String(metadata.toLocale)}`);
    if (metadata.suggestedLocale) parts.push(`Suggested: ${String(metadata.suggestedLocale)}`);
    if (metadata.via) parts.push(`Via: ${String(metadata.via)}`);
    if (metadata.path) parts.push(`On: ${String(metadata.path)}`);
    return parts.join(" · ");
  }
  if (eventType === "field_updated" && typeof metadata.field === "string") {
    const label = PROGRESS_FIELD_LABELS[metadata.field] ?? metadata.field;
    if (metadata.cleared) return `${label}: (cleared)`;
    const value =
      typeof metadata.value === "boolean"
        ? metadata.value
          ? "Yes"
          : "No"
        : String(metadata.value ?? "");
    const extras = Object.entries(metadata)
      .filter(([key]) => key !== "field" && key !== "value" && key !== "cleared")
      .slice(0, 3)
      .map(([key, val]) => `${key}: ${String(val)}`);
    return [`${label}: ${value}`, ...extras].filter(Boolean).join(" · ");
  }
  return Object.entries(metadata)
    .slice(0, 6)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(" · ");
}

function SortHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SessionSortKey;
  activeKey: SessionSortKey;
  direction: SortDirection;
  onSort: (key: SessionSortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th className="whitespace-nowrap px-3 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-brand-600 ${
          active ? "text-brand-600" : ""
        }`}
      >
        {label}
        <span className="text-xs opacity-70">
          {active ? (direction === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

function CellStack({
  primary,
  secondary,
  tertiary,
  title,
}: {
  primary: string;
  secondary?: string;
  tertiary?: string;
  title?: string;
}) {
  return (
    <div className="min-w-0" title={title}>
      <div className="truncate font-medium text-gray-800 dark:text-white/90">
        {primary}
      </div>
      {secondary ? (
        <div className="mt-0.5 truncate text-xs text-gray-500">{secondary}</div>
      ) : null}
      {tertiary ? (
        <div className="mt-0.5 truncate text-xs text-gray-500">{tertiary}</div>
      ) : null}
    </div>
  );
}

function CustomerProgressPanel({
  progress,
  session,
  vehicleNames,
}: {
  progress: Record<string, unknown>;
  session: AuditSession;
  vehicleNames?: Record<string, string>;
}) {
  const entries = getFormattedProgressEntries(progress, {
    events: session.events,
    vehicleNames,
  });
  const email =
    session.customerEmail ??
    (typeof progress.email === "string" ? progress.email : "") ??
    "";
  const hasUsableEmail =
    typeof email === "string" &&
    email.includes("@") &&
    !/^\S\*\*\*@/.test(email.trim());
  const resumeUrl = !session.completed
    ? buildAbandonedSessionResumeUrl(session.bookingSource, progress)
    : null;
  const canCreateBooking = hasAuditCreateBookingData(progress);
  const siteName =
    BOOKING_SOURCE_SITE_NAMES[session.bookingSource] ?? session.bookingSource;
  const routeSummary = getRouteSummary(progress);
  const [linkStatus, setLinkStatus] = useState<string>("");
  const [copiedFieldKey, setCopiedFieldKey] = useState<string | null>(null);

  const handleCopyLink = async () => {
    if (!resumeUrl) return;
    const ok = await copyTextToClipboard(resumeUrl);
    setLinkStatus(ok ? "Resume link copied" : "Could not copy link");
    window.setTimeout(() => setLinkStatus(""), 2500);
  };

  const handleCopyField = async (key: string, value: string) => {
    const text = value.trim();
    if (!text || text === "—") return;
    const ok = await copyTextToClipboard(text);
    setCopiedFieldKey(ok ? key : null);
    setLinkStatus(ok ? "" : "Could not copy field");
    window.setTimeout(() => {
      setCopiedFieldKey((current) => (current === key ? null : current));
      setLinkStatus("");
    }, 2000);
  };

  if (entries.length === 0 && !email && !resumeUrl && !canCreateBooking) {
    return (
      <p className="text-sm text-gray-500">
        No form fields captured yet — visitor opened the form but did not enter details.
      </p>
    );
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-4 dark:border-brand-500/30 dark:bg-brand-500/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white/90">
          Customer entered
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {canCreateBooking && (
            <Link
              to="/bookings/add"
              state={buildAuditCreateBookingState(
                progress,
                session.bookingSource,
              )}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-400"
            >
              Create booking
            </Link>
          )}
          {!session.completed && (
            <>
              <button
                type="button"
                onClick={() => void handleCopyLink()}
                disabled={!resumeUrl}
                className="rounded-lg border border-brand-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-brand-500/40 dark:bg-gray-900 dark:text-brand-300"
              >
                Copy resume link
              </button>
              {resumeUrl && (
                <a
                  href={buildAbandonedBookingMailto({
                    to: hasUsableEmail ? email : null,
                    siteName,
                    resumeUrl,
                    routeSummary: routeSummary !== "—" ? routeSummary : undefined,
                  })}
                  className="rounded-lg border border-brand-300 bg-white px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-500/40 dark:bg-gray-900 dark:text-brand-300"
                >
                  Email customer
                </a>
              )}
            </>
          )}
          {linkStatus && (
            <span className="text-xs text-green-700 dark:text-green-400">{linkStatus}</span>
          )}
        </div>
      </div>
      {resumeUrl && (
        <p className="mt-2 text-xs text-gray-500 break-all">
          Prefill link: {resumeUrl}
        </p>
      )}
      {!resumeUrl && !session.completed && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          Need at least one captured field (pickup, date, contact, etc.) to build a resume link.
        </p>
      )}
      <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map((entry) => {
          const canCopy = Boolean(entry.value?.trim()) && entry.value !== "—";
          const justCopied = copiedFieldKey === entry.key;
          return (
            <div
              key={entry.key}
              className="rounded-lg border border-white/80 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40"
            >
              <div className="flex items-start justify-between gap-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {entry.label}
                </dt>
                {canCopy ? (
                  <button
                    type="button"
                    onClick={() => void handleCopyField(entry.key, entry.value)}
                    className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    aria-label={`Copy ${entry.label}`}
                    title={`Copy ${entry.label}`}
                  >
                    {justCopied ? "Copied" : "Copy"}
                  </button>
                ) : null}
              </div>
              <dd className="mt-1 text-sm text-gray-800 break-words dark:text-white/90">
                {entry.value}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

interface Props {
  sessions: AuditSession[];
  sessionTotal?: number;
  stats: BookingFormAuditStats | null;
  loading: boolean;
  error: string;
  sourceOptions: [string, string][];
  bookingSource: string;
  fromDate: string;
  toDate: string;
  customerEmail: string;
  sessionId: string;
  onBookingSourceChange: (value: string) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onCustomerEmailChange: (value: string) => void;
  onSessionIdChange: (value: string) => void;
  onRefresh: () => void;
  vehicleNames?: Record<string, string>;
  clientFilters: SessionClientFilters;
  onClientFiltersChange: (filters: SessionClientFilters) => void;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export default function BookingFormAuditSessionTable({
  sessions,
  sessionTotal,
  stats,
  loading,
  error,
  sourceOptions,
  bookingSource,
  fromDate,
  toDate,
  customerEmail,
  sessionId,
  onBookingSourceChange,
  onFromDateChange,
  onToDateChange,
  onCustomerEmailChange,
  onSessionIdChange,
  onRefresh,
  vehicleNames,
  clientFilters,
  onClientFiltersChange,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SessionSortKey>("lastEventAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  useEffect(() => {
    setExpandedSession(null);
  }, [clientFilters, sessions, currentPage, pageSize]);

  const handleSort = (key: SessionSortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "startedAt" || key === "lastEventAt" ? "desc" : "asc");
  };

  const totalSessionCount = sessionTotal ?? sessions.length;
  const totalPages = Math.max(1, Math.ceil(totalSessionCount / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const rangeStart =
    totalSessionCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalSessionCount);

  // Server already filtered; keep a light client pass for route / phone / name
  // filters that are not yet expressed in SQL. Do not re-apply country/audience
  // defaults here — that was locking the list to UK even after clearing filters.
  const filteredSessions = useMemo(
    () =>
      filterSessions(sessions, {
        ...DEFAULT_SESSION_FILTERS,
        country: "",
        route: clientFilters.route,
        contact:
          clientFilters.contact === "has_phone" ||
          clientFilters.contact === "has_name"
            ? clientFilters.contact
            : "all",
      }),
    [sessions, clientFilters.route, clientFilters.contact],
  );

  const displayedSessions = useMemo(
    () => sortSessions(filteredSessions, sortKey, sortDirection),
    [filteredSessions, sortKey, sortDirection],
  );

  const pageNumbers = useMemo(
    () => buildPageNumbers(safePage, totalPages),
    [safePage, totalPages],
  );

  const paginatedSessions = displayedSessions;

  const updateFilter = <K extends keyof SessionClientFilters>(
    key: K,
    value: SessionClientFilters[K],
  ) => {
    onClientFiltersChange({ ...clientFilters, [key]: value });
  };

  const resetClientFilters = () => onClientFiltersChange(DEFAULT_SESSION_FILTERS);

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; clear: () => void }[] = [];
    if (clientFilters.audience !== "leads") {
      chips.push({
        label:
          clientFilters.audience === "bots" ? "Bots only" : "All traffic",
        clear: () => updateFilter("audience", "leads"),
      });
    }
    if (clientFilters.status !== "all") {
      chips.push({
        label: clientFilters.status === "completed" ? "Completed" : "Abandoned",
        clear: () => updateFilter("status", "all"),
      });
    }
    if (clientFilters.lastStep) {
      chips.push({
        label: `Stopped at ${auditStepLabel(clientFilters.lastStep)}`,
        clear: () => updateFilter("lastStep", ""),
      });
    }
    if (clientFilters.reachedStep) {
      chips.push({
        label: `Reached ${auditStepLabel(clientFilters.reachedStep)}`,
        clear: () => updateFilter("reachedStep", ""),
      });
    }
    if (clientFilters.cookieConsent) {
      chips.push({
        label: "Cookie consent",
        clear: () => updateFilter("cookieConsent", false),
      });
    }
    if (clientFilters.hadValidationError) {
      chips.push({
        label: "Validation errors",
        clear: () => updateFilter("hadValidationError", false),
      });
    }
    if (clientFilters.hadFareQuoteFailure) {
      chips.push({
        label: "Fare quote failed",
        clear: () => updateFilter("hadFareQuoteFailure", false),
      });
    }
    if (clientFilters.hadPaymentFailure) {
      chips.push({
        label: "Payment failed",
        clear: () => updateFilter("hadPaymentFailure", false),
      });
    }
    if (clientFilters.cardEntry) {
      chips.push({
        label:
          clientFilters.cardEntry === "completed"
            ? "Card details entered"
            : clientFilters.cardEntry === "error"
              ? "Card input error"
              : "Reached payment, no card",
        clear: () => updateFilter("cardEntry", ""),
      });
    }
    if (clientFilters.hadBookingFailure) {
      chips.push({
        label: "Booking failed",
        clear: () => updateFilter("hadBookingFailure", false),
      });
    }
    if (clientFilters.deviceType) {
      chips.push({
        label: `Device: ${clientFilters.deviceType}`,
        clear: () => updateFilter("deviceType", ""),
      });
    }
    if (clientFilters.country) {
      chips.push({
        label: `Country: ${clientFilters.country}`,
        clear: () => updateFilter("country", ""),
      });
    }
    if (clientFilters.browser) {
      chips.push({
        label: `Browser: ${clientFilters.browser}`,
        clear: () => updateFilter("browser", ""),
      });
    }
    if (clientFilters.trafficSource) {
      chips.push({
        label: `Traffic: ${trafficSourceLabel(clientFilters.trafficSource)}`,
        clear: () => updateFilter("trafficSource", ""),
      });
    }
    if (clientFilters.landingPage) {
      chips.push({
        label: `Landing: ${clientFilters.landingPage}`,
        clear: () => updateFilter("landingPage", ""),
      });
    }
    return chips;
  }, [clientFilters]);

  return (
    <>
    <div
      id="audit-session-list"
      className="audit-no-print space-y-3 lg:space-y-0 lg:rounded-2xl lg:border lg:border-gray-200 lg:bg-white dark:lg:border-gray-800 dark:lg:bg-white/[0.03]"
    >
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] sm:p-5 lg:rounded-none lg:border-0 lg:border-b lg:shadow-none dark:lg:border-gray-800">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
          Site activity
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Sort and filter sessions. Expand a row to see pages explored, clicks, and anything
          entered in the booking form.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select
            value={bookingSource}
            onChange={(e) => onBookingSourceChange(e.target.value)}
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="">All websites</option>
            {sourceOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <div className="w-[10.5rem]">
            <Input
              type="date"
              placeholder="From date"
              value={fromDate}
              onChange={(e) => onFromDateChange(e.target.value)}
            />
          </div>
          <div className="w-[10.5rem]">
            <Input
              type="date"
              placeholder="To date"
              value={toDate}
              onChange={(e) => onToDateChange(e.target.value)}
            />
          </div>
          <div className="min-w-[12rem] w-52 max-w-full">
            <Input
              placeholder="Customer email"
              value={customerEmail}
              onChange={(e) => onCustomerEmailChange(e.target.value)}
            />
          </div>
          <div className="min-w-[10rem] w-44 max-w-full">
            <Input
              placeholder="Session ID"
              value={sessionId}
              onChange={(e) => onSessionIdChange(e.target.value)}
            />
          </div>
            <button
              type="button"
              onClick={onRefresh}
              className="h-11 shrink-0 rounded-lg bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={triggerAuditReportPrint}
              disabled={loading || totalSessionCount === 0}
              className="h-11 shrink-0 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-white/90"
            >
              Print report
            </button>
          </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select
            value={clientFilters.audience}
            onChange={(e) =>
              updateFilter(
                "audience",
                e.target.value as SessionClientFilters["audience"],
              )
            }
            className="h-11 w-full min-w-[12rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="leads">Leads / real customers</option>
            <option value="all">All traffic</option>
            <option value="bots">Bots / scrapers</option>
          </select>

          <select
            value={clientFilters.status}
            onChange={(e) =>
              updateFilter("status", e.target.value as SessionClientFilters["status"])
            }
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="all">All statuses</option>
            <option value="completed">Completed only</option>
            <option value="abandoned">Abandoned only</option>
          </select>

          <select
            value={clientFilters.lastStep}
            onChange={(e) => updateFilter("lastStep", e.target.value)}
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="">All steps</option>
            {BOOKING_FUNNEL_STEPS.map((value) => (
              <option key={value} value={value}>
                Stopped at {BOOKING_STEP_LABELS[value]}
              </option>
            ))}
          </select>

          <select
            value={clientFilters.contact}
            onChange={(e) =>
              updateFilter("contact", e.target.value as SessionClientFilters["contact"])
            }
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="all">Any contact info</option>
            <option value="has_email">Has email</option>
            <option value="has_phone">Has phone</option>
            <option value="has_name">Has name</option>
          </select>

          <select
            value={clientFilters.route}
            onChange={(e) =>
              updateFilter("route", e.target.value as SessionClientFilters["route"])
            }
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="all">Any route</option>
            <option value="has_pickup">Has pickup</option>
            <option value="has_route">Has pickup + drop-off</option>
          </select>

          <select
            value={clientFilters.cardEntry}
            onChange={(e) =>
              updateFilter(
                "cardEntry",
                e.target.value as SessionClientFilters["cardEntry"],
              )
            }
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="">Card entry: any</option>
            <option value="completed">Card details entered</option>
            <option value="not_completed">Reached payment, no card</option>
            <option value="error">Card input error</option>
          </select>

          <div className="min-w-[14rem] w-64 max-w-full">
            <Input
              placeholder="Search route, email, name…"
              value={clientFilters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </div>

          <div className="min-w-[12rem] w-56 max-w-full">
            <Input
              placeholder="Landing page contains"
              value={clientFilters.landingPage}
              onChange={(e) => updateFilter("landingPage", e.target.value)}
            />
          </div>

          <select
            value={clientFilters.trafficSource}
            onChange={(e) => updateFilter("trafficSource", e.target.value)}
            className="h-11 w-full min-w-[11rem] max-w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:w-auto"
          >
            <option value="">All traffic sources</option>
            <option value="google_organic">Google Organic</option>
            <option value="google_ads">Google Ads</option>
            <option value="bing_organic">Bing Organic</option>
            <option value="ai_referral">AI / ChatGPT</option>
            <option value="social">Social</option>
            <option value="referral">Referral</option>
            <option value="direct">Direct</option>
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={clientFilters.hadValidationError}
              onChange={(e) => updateFilter("hadValidationError", e.target.checked)}
            />
            Validation errors
          </label>
          <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={clientFilters.hadFareQuote}
              onChange={(e) => updateFilter("hadFareQuote", e.target.checked)}
            />
            Fare quote attempted
          </label>
          <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={clientFilters.reachedPayment}
              onChange={(e) => updateFilter("reachedPayment", e.target.checked)}
            />
            Reached payment
          </label>
          <button
            type="button"
            onClick={resetClientFilters}
            className="text-brand-600 hover:underline"
          >
            Clear filters
          </button>
          {activeFilterChips.length > 0 && (
            <div className="flex w-full flex-wrap items-center gap-2">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  onClick={chip.clear}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-500/10 dark:text-brand-300"
                >
                  {chip.label}
                  <span aria-hidden>×</span>
                </button>
              ))}
            </div>
          )}
          <span className="text-gray-500">
            {totalSessionCount === 0
              ? "0 sessions"
              : `${rangeStart}–${rangeEnd} of ${totalSessionCount} sessions`}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-700 lg:rounded-none lg:border-x-0 lg:border-t-0">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:rounded-none lg:border-0 lg:shadow-none">
          Loading audit logs…
        </div>
      ) : totalSessionCount === 0 || paginatedSessions.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:rounded-none lg:border-0 lg:shadow-none">
          No sessions match the selected filters.
        </div>
      ) : (
        <>
          <BookingFormAuditSessionCards
            sessions={paginatedSessions}
            expandedSession={expandedSession}
            onToggle={(sessionId) =>
              setExpandedSession((current) =>
                current === sessionId ? null : sessionId,
              )
            }
            renderProgress={(session, progress) => (
              <CustomerProgressPanel
                progress={progress}
                session={session}
                vehicleNames={vehicleNames}
              />
            )}
            vehicleNames={vehicleNames}
          />

          <div className="hidden min-w-0 overflow-hidden lg:block">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[11%]" />
              <col className="w-[13%]" />
              <col className="w-[16%]" />
              <col className="w-[13%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[8%]" />
              <col className="w-[6%]" />
              <col className="w-[7%]" />
            </colgroup>
            <thead className="bg-gray-50 text-left text-gray-600 dark:bg-gray-900/40">
              <tr>
                {TABLE_COLUMNS.map((col) => (
                  <SortHeader
                    key={col.key}
                    label={col.label}
                    sortKey={col.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                  />
                ))}
                <th className="whitespace-nowrap px-3 py-3 font-medium">Booking</th>
                <th className="px-3 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {paginatedSessions.map((session) => {
                const expanded = expandedSession === session.sessionId;
                const progress = getSessionCustomerProgress(session);
                const route = getRouteSummary(progress);
                const customerName = getCustomerDisplayName(progress);
                const email =
                  (session.customerEmail ?? String(progress.email ?? "")) || "—";
                const phone = progress.mobile ? String(progress.mobile) : "";
                const fareValue = formatAmount(progress.fare, 0);
                const fare = fareValue === "N/A" ? "—" : fareValue;
                const cardStatus = getCardEntryStatus(session);
                const lastStep = session.lastStepName
                  ? auditStepLabel(session.lastStepName)
                  : "—";
                const landing = formatLandingPage(session.visitor);
                const traffic = formatTrafficSource(session.visitor);

                return (
                  <Fragment key={session.sessionId}>
                    <tr className="border-t border-gray-100 dark:border-gray-800 align-top">
                      <td className="px-3 py-3">
                        <div
                          className="truncate text-gray-800 dark:text-white/90"
                          title={formatSource(session.bookingSource)}
                        >
                          {formatSource(session.bookingSource)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <CellStack
                          primary={formatDateTime(session.lastEventAt)}
                          secondary={`Started ${formatDateTime(session.startedAt)}`}
                          tertiary={`${session.eventCount} events`}
                          title={`Last: ${formatDateTime(session.lastEventAt)} · Started: ${formatDateTime(session.startedAt)}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <CellStack
                          primary={`${lastStep}${fare !== "—" ? ` · ${fare}` : ""}`}
                          secondary={route}
                          title={`${lastStep} · ${fare} · ${route}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <CellStack
                          primary={customerName}
                          secondary={email}
                          tertiary={phone || undefined}
                          title={[customerName, email, phone].filter(Boolean).join(" · ")}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <CellStack
                          primary={formatLocation(session.visitor)}
                          secondary={formatDevice(session.visitor)}
                          tertiary={formatEngagement(session.visitor)}
                          title={`${formatLocation(session.visitor)} · ${formatDevice(session.visitor)} · ${formatEngagement(session.visitor)}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <CellStack
                          primary={traffic}
                          secondary={landing}
                          title={`${traffic} · ${landing}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            session.completed
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {session.completed ? "Completed" : "Abandoned"}
                        </span>
                        {cardStatus !== "not_reached" && (
                          <div
                            className={`mt-1 truncate text-[11px] font-medium ${
                              cardStatus === "completed"
                                ? "text-green-600 dark:text-green-400"
                                : cardStatus === "error"
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-amber-600 dark:text-amber-400"
                            }`}
                          >
                            {CARD_ENTRY_STATUS_LABELS[cardStatus]}
                          </div>
                        )}
                      </td>
                      <td className="truncate px-3 py-3">
                        {session.bookingDirectId ? (
                          <Link
                            to={`/bookings/${session.bookingDirectId}/view`}
                            className="text-brand-600 hover:underline"
                          >
                            #{session.bookingId ?? "View"}
                          </Link>
                        ) : hasAuditCreateBookingData(progress) ? (
                          <Link
                            to="/bookings/add"
                            state={buildAuditCreateBookingState(
                              progress,
                              session.bookingSource,
                            )}
                            className="text-brand-600 hover:underline"
                          >
                            Create
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedSession(
                              expanded ? null : session.sessionId,
                            )
                          }
                          className="text-brand-600 hover:underline"
                        >
                          {expanded ? "Hide" : "Details"}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-t border-gray-100 bg-gray-50/70 dark:border-gray-800 dark:bg-gray-900/20">
                        <td colSpan={TABLE_COL_COUNT} className="px-5 py-4">
                          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span>Session: {session.sessionId}</span>
                            <button
                              type="button"
                              onClick={() => void copyTextToClipboard(session.sessionId)}
                              className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                              aria-label="Copy session ID"
                              title="Copy session ID"
                            >
                              Copy ID
                            </button>
                            <CopyAuditJourneyButton
                              session={session}
                              vehicleNames={vehicleNames}
                            />
                          </div>

                          <CustomerProgressPanel
                            progress={progress}
                            session={session}
                            vehicleNames={vehicleNames}
                          />

                          {session.visitor && (
                            <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40">
                              <div className="font-medium text-gray-800 dark:text-white/90">
                                Visitor context
                              </div>
                              <div className="mt-2 grid gap-2 text-xs text-gray-600 sm:grid-cols-2 lg:grid-cols-3">
                                <span>Location: {formatLocation(session.visitor)}</span>
                                <span>Device: {formatDevice(session.visitor)}</span>
                                <span>IP: {session.visitor.ipAddress ?? "—"}</span>
                                <span>Language: {session.visitor.language ?? "—"}</span>
                                <span>Timezone: {session.visitor.timezone ?? "—"}</span>
                                <span>
                                  Screen:{" "}
                                  {session.visitor.screenWidth &&
                                  session.visitor.screenHeight
                                    ? `${session.visitor.screenWidth}×${session.visitor.screenHeight}`
                                    : "—"}
                                </span>
                                <span>Geo source: {session.visitor.geoSource ?? "—"}</span>
                                <span>
                                  Traffic: {formatTrafficSource(session.visitor)}
                                </span>
                                <span>
                                  Referrer: {session.visitor.referrer || "Direct"}
                                </span>
                                {session.visitor.utmSource ? (
                                  <span>UTM source: {session.visitor.utmSource}</span>
                                ) : null}
                                {session.visitor.utmMedium ? (
                                  <span>UTM medium: {session.visitor.utmMedium}</span>
                                ) : null}
                                {session.visitor.utmCampaign ? (
                                  <span>UTM campaign: {session.visitor.utmCampaign}</span>
                                ) : null}
                                {session.visitor.utmTerm ? (
                                  <span>UTM term: {session.visitor.utmTerm}</span>
                                ) : null}
                                {session.visitor.gclid ? (
                                  <span className="break-all">gclid: {session.visitor.gclid}</span>
                                ) : null}
                                <span>
                                  Engagement: {formatEngagement(session.visitor)}
                                </span>
                                <span className="sm:col-span-2 lg:col-span-3 break-all">
                                  Landing: {session.visitor.landingPage || "—"}
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="mt-4">
                            <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-white/90">
                              Event timeline
                            </h4>
                            <ol className="space-y-2">
                              {session.events.map((event) => (
                                <li
                                  key={event.id}
                                  className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40"
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-gray-800 dark:text-white/90">
                                      {formatEventType(event.eventType)}
                                    </span>
                                    {event.stepName && (
                                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                        {auditStepLabel(event.stepName)}
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-500">
                                      {formatDateTime(event.createdAt)}
                                    </span>
                                  </div>
                                  {metadataPreview(event.metadata, event.eventType) && (
                                    <p className="mt-1 text-xs text-gray-500 break-words">
                                      {metadataPreview(event.metadata, event.eventType)}
                                    </p>
                                  )}
                                </li>
                              ))}
                            </ol>
                          </div>
                        
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      )}

      {!loading && totalSessionCount > 0 && paginatedSessions.length > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between sm:px-5 lg:rounded-none lg:border-0 lg:border-t lg:shadow-none dark:lg:border-gray-800">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <label htmlFor="audit-page-size" className="whitespace-nowrap">
              Rows per page
            </label>
            <select
              id="audit-page-size"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-11 rounded-xl border border-gray-300 bg-transparent px-2 text-sm text-gray-800 dark:border-gray-700 dark:text-white/90 sm:h-9 sm:rounded-lg"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => onPageChange(Math.max(1, safePage - 1))}
              className={`${adminPaginationInactiveClass} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              Previous
            </button>
            {pageNumbers.map((page, index) => {
              const prev = pageNumbers[index - 1];
              const showEllipsis = prev != null && page - prev > 1;
              return (
                <Fragment key={page}>
                  {showEllipsis && (
                    <span className="px-2 text-sm text-gray-400">…</span>
                  )}
                  <button
                    type="button"
                    onClick={() => onPageChange(page)}
                    className={
                      safePage === page
                        ? adminPaginationActiveClass
                        : adminPaginationInactiveClass
                    }
                  >
                    {page}
                  </button>
                </Fragment>
              );
            })}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
              className={`${adminPaginationInactiveClass} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>

    <BookingFormAuditPrintReport
      stats={stats}
      sessions={displayedSessions}
      sessionCountTotal={totalSessionCount}
      vehicleNames={vehicleNames}
      apiFilters={{
        bookingSource,
        fromDate,
        toDate,
        customerEmail,
        sessionId,
      }}
      clientFilters={clientFilters}
    />
    </>
  );
}
